/**
 * FCM Notification Context
 * 
 * Centralized context for managing FCM notification state and handlers.
 * Provides a single source of truth for notification events across the app.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import useFCM from '../Hooks/useFCM';

const FCMNotificationContext = createContext(null);

export const useFCMNotifications = () => {
  const context = useContext(FCMNotificationContext);
  if (!context) {
    throw new Error('useFCMNotifications must be used within FCMNotificationProvider');
  }
  return context;
};

export const FCMNotificationProvider = ({ children }) => {
  const { addEventListener, isInitialized } = useFCM();
  const [notificationCount, setNotificationCount] = useState(0);

  // Use refs to avoid stale closures in event handlers
  const messageHandlersRef = useRef([]);
  const clickHandlersRef = useRef([]);

  /**
   * Register a handler for message-received events
   * Returns unsubscribe function
   */
  const onMessageReceived = useCallback((handler) => {
    const id = Date.now() + Math.random();
    const handlerObj = { id, handler };
    messageHandlersRef.current = [...messageHandlersRef.current, handlerObj];
    
    return () => {
      messageHandlersRef.current = messageHandlersRef.current.filter(h => h.id !== id);
    };
  }, []);

  /**
   * Register a handler for notification-clicked events
   * Returns unsubscribe function
   */
  const onNotificationClicked = useCallback((handler) => {
    const id = Date.now() + Math.random();
    const handlerObj = { id, handler };
    clickHandlersRef.current = [...clickHandlersRef.current, handlerObj];
    
    return () => {
      clickHandlersRef.current = clickHandlersRef.current.filter(h => h.id !== id);
    };
  }, []);

  /**
   * Increment notification count (optimistic update)
   */
  const incrementNotificationCount = useCallback(() => {
    setNotificationCount(prev => prev + 1);
  }, []);

  /**
   * Update notification count from server
   */
  const updateNotificationCount = useCallback((count) => {
    setNotificationCount(count);
  }, []);

  // Set up FCM event listeners (single source)
  useEffect(() => {
    if (!isInitialized) return;

    // Handle message received - notify all registered handlers
    const unsubscribeMessage = addEventListener('message-received', (event) => {
      const payload = event.detail?.data || event.detail;
      
      // Use ref to get current handlers (avoids stale closure)
      messageHandlersRef.current.forEach(({ handler }) => {
        try {
          handler(payload);
        } catch (error) {
          console.error('[FCMContext] Error in message handler:', error);
        }
      });
    });

    // Handle notification clicked - notify all registered handlers
    const unsubscribeClick = addEventListener('notification-clicked', (event) => {
      const data = event.detail?.data || event.detail;
      
      // Use ref to get current handlers (avoids stale closure)
      clickHandlersRef.current.forEach(({ handler }) => {
        try {
          handler(data);
        } catch (error) {
          console.error('[FCMContext] Error in click handler:', error);
        }
      });
    });

    // Listen for service worker messages (background notifications)
    const handleServiceWorkerMessage = (event) => {
      if (!event?.data?.type) return;

      if (event.data.type === 'ARIS_NOTIFICATION_RECEIVED') {
        const payload = event.data.payload || {};
        
        // Use ref to get current handlers (avoids stale closure)
        messageHandlersRef.current.forEach(({ handler }) => {
          try {
            handler(payload);
          } catch (error) {
            console.error('[FCMContext] Error in SW message handler:', error);
          }
        });
      }

      if (event.data.type === 'ARIS_NOTIFICATION_CLICKED') {
        const data = event.data.data || {};
        
        // Use ref to get current handlers (avoids stale closure)
        clickHandlersRef.current.forEach(({ handler }) => {
          try {
            handler(data);
          } catch (error) {
            console.error('[FCMContext] Error in SW click handler:', error);
          }
        });
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      unsubscribeMessage();
      unsubscribeClick();
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [isInitialized, addEventListener]);

  const value = {
    // State
    notificationCount,
    isInitialized,
    
    // Methods
    onMessageReceived,
    onNotificationClicked,
    incrementNotificationCount,
    updateNotificationCount,
  };

  return (
    <FCMNotificationContext.Provider value={value}>
      {children}
    </FCMNotificationContext.Provider>
  );
};

export default FCMNotificationContext;
